/**
 * What the browser does with a file (spec 8.1; IP-91, IP-93), in three steps:
 *
 *   1. prepareUpload - read its bytes and fingerprint them (SHA-256, hex):
 *      the six facts INTAKE_UPLOAD's code is bound to. The bytes stay here.
 *   2. postToStore - the Console API answers the upload with a TICKET: the
 *      batch, created SCANNING, and a presigned POST good for 300 s and for
 *      exactly this key, length, encryption key and SHA-256. The browser
 *      posts the bytes straight to the intake bucket on that form - never
 *      through the API, whose firewall refuses a body over 8 KB (IP-91
 *      supersedes IP-44). S3 itself refuses bytes whose SHA-256 is not the
 *      one the code was bound to.
 *   3. createScanWatch - GuardDuty scans the object and the verdict's
 *      consumer (services/intake) re-hashes and parses it; the screen polls
 *      the SCANNING list, one call per tick for every upload it waits on,
 *      backing off, for at most 3 minutes.
 *
 * A ticket whose form is never posted, or posted and refused, leaves its
 * batch SCANNING with no verdict: it lapses to SCAN_FAILED 30 minutes after
 * the upload (IP-93), and the same file can be uploaded again then.
 *
 * A file the scan cleared can be fetched back, exactly as uploaded, through
 * the Console API (GET intake/batches/{id}/file): downloadNotice says which
 * may be (the server's own test, batch.js isDownloadable), and
 * downloadedBytes decodes the answer and checks its SHA-256 before the file
 * is saved.
 */
import { ACCEPTANCE_EXPIRY_MS, SCAN_VERDICT_TIMEOUT_MS, isDownloadable } from '@lfoc/domain/intake/batch.js';

import { SYSTEM_ACCEPTOR, acceptedBySystem, dateProblem, dateRule, takesLoadKind } from './uploadKinds.js';

/** The Console API's cap: 4 MB (catalogue.js MAX_UPLOAD_BYTES). */
export const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

/** IP-93: how long a batch with no verdict stays SCANNING, in minutes - for the words a person reads. */
export const SCAN_LAPSE_MINUTES = Math.round(SCAN_VERDICT_TIMEOUT_MS / 60000);

/** The classes (IP-103; migration 0026's WM files and 0014's store-only ones), in the order the screen lists them, each with the name its file usually has. */
export const FILE_CLASSES = Object.freeze([
  { value: 'NAV_VALUES', label: 'NAV values (nav_values.csv)' },
  { value: 'BANK_STATEMENT', label: 'Bank statement (bank_transaction.csv)' },
  { value: 'CALENDAR', label: 'Calendar (calendar.csv)' },
  { value: 'UNIT_PRICES', label: 'Unit prices (unit-prices-initial-load.csv)' },
  { value: 'HOLDINGS', label: 'Holdings (daily-client-holdings.csv)' },
  { value: 'TRANSACTIONS', label: 'Transactions (daily-transactions.csv)' },
  { value: 'WM_HOLDINGS', label: 'WM Holdings (wm-holdings-daily.csv)' },
  { value: 'WM_TRANSACTIONS', label: 'WM Transactions (wm-transactions-daily.csv)' },
  { value: 'BENCHMARK', label: 'Daily benchmark (daily-benchmark.csv)' },
  { value: 'INCOME_EXPENSE', label: 'Income and expense breakdown (income-expense-breakdown.csv)' },
  { value: 'SECURITIES_UTDOF', label: 'UTDOF security holdings (utdof-security-holdings.csv)' },
  { value: 'SECURITIES_UTIF', label: 'UTIF security holdings (utif-security-holdings.csv)' },
  { value: 'SECURITIES_UTLMF', label: 'UTLMF security holdings (utlmf-security-holdings.csv)' },
  { value: 'SECURITIES_UTOYF', label: 'UTOYF security holdings (utoyf-security-holdings.csv)' },
  { value: 'FUND_FACTS_UTDOF', label: 'UTDOF fund facts (utdof-fund-facts.csv)' },
  { value: 'FUND_FACTS_UTIF', label: 'UTIF fund facts (utif-fund-facts.csv)' },
  { value: 'FUND_FACTS_UTLMF', label: 'UTLMF fund facts (utlmf-fund-facts.csv)' },
  { value: 'FUND_FACTS_UTOYF', label: 'UTOYF fund facts (utoyf-fund-facts.csv)' },
]);

/**
 * What a file's name calls each class: the names the files are exported
 * under - LIV's daily-* files among them (design 2026-10-02 section 8) - and
 * the class's own. First match wins, in this order.
 */
const CLASS_NAMES = Object.freeze({
  NAV_VALUES: ['nav_values'],
  BANK_STATEMENT: ['bank_transaction', 'bank_statement'],
  CALENDAR: ['calendar'],
  // The WM clients' files (migration 0026), before the register's own: a wm-* name is never the register's file.
  WM_HOLDINGS: ['wm_holdings'],
  WM_TRANSACTIONS: ['wm_transactions'],
  HOLDINGS: ['daily_client_holdings'],
  TRANSACTIONS: ['daily_transactions'],
  // The analytics files (migration 0014), by the prefixes they are exported under.
  BENCHMARK: ['daily_benchmark'],
  INCOME_EXPENSE: ['income_expense_breakdown'],
  SECURITIES_UTDOF: ['utdof_security_holdings'],
  SECURITIES_UTIF: ['utif_security_holdings'],
  SECURITIES_UTLMF: ['utlmf_security_holdings'],
  SECURITIES_UTOYF: ['utoyf_security_holdings'],
  FUND_FACTS_UTDOF: ['utdof_fund_facts'],
  FUND_FACTS_UTIF: ['utif_fund_facts'],
  FUND_FACTS_UTLMF: ['utlmf_fund_facts'],
  FUND_FACTS_UTOYF: ['utoyf_fund_facts'],
});

/** The names that are daily files - LIV's, and the WM templates' (wm-*-daily): a file so named is a DAILY load. */
const DAILY_NAMES = Object.freeze(['daily_client_holdings', 'daily_transactions', 'wm_holdings_daily', 'wm_transactions_daily']);

const normalName = (fileName) => String(fileName ?? '').toLowerCase().replace(/[\s-]+/g, '_');

/**
 * The class a file's name says it is - `nav_values_2026-09-30.csv` is NAV
 * values, `daily-client-holdings.csv` holdings - or null when the name says
 * nothing. Case, spaces and hyphens are not told apart from underscores.
 */
export function fileClassOf(fileName) {
  const name = normalName(fileName);
  const found = Object.entries(CLASS_NAMES).find(([, names]) => names.some((n) => name.includes(n)));
  return found ? found[0] : null;
}

/**
 * What a file's name says of the upload: its class, and DAILY for one of
 * LIV's daily-* files (a calendar's name says no kind) - or null.
 *
 * @returns {{ fileClass: string, loadKind: 'DAILY'|null } | null}
 */
export function guessUpload(fileName) {
  const fileClass = fileClassOf(fileName);
  if (!fileClass) return null;
  const name = normalName(fileName);
  return { fileClass, loadKind: DAILY_NAMES.some((n) => name.includes(n)) ? 'DAILY' : null };
}

/**
 * Whether the upload dialog must (re)load the collection accounts: it is
 * open, for a bank statement, and has none. Asked again each time a file is
 * chosen (and on Try again), so one failed load - said beside the account
 * field, not in the dialog's own error - is retried rather than leaving an
 * empty list and no reason.
 *
 * @param {{ open: boolean, fileClass: string|undefined, loaded: number }} state
 */
export function needsAccounts({ open, fileClass, loaded }) {
  return Boolean(open) && fileClass === 'BANK_STATEMENT' && !loaded;
}

/** Today in Asia/Colombo, as YYYY-MM-DD - the business date the server checks against. */
export function colomboToday(at = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Colombo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
}

export async function sha256Hex(bytes) {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The bytes a download carries as base64. (The upload no longer encodes
 * anything: its bytes go to the bucket as they are - IP-91.)
 */
export function bytesOfBase64(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Why a batch's file cannot be downloaded, in words for the row's menu, or
 * null when it can - by the server's own test (batch.js isDownloadable), so
 * the menu never offers what GET intake/batches/{id}/file would refuse.
 */
export function downloadNotice(batch) {
  if (isDownloadable(batch)) return null;
  switch (batch?.state) {
    case 'RECEIVED':
    case 'SCANNING':
      return 'Not yet: it is still being scanned for malware.';
    case 'REJECTED_MALWARE':
      return 'Never: the malware scan found a threat in it.';
    case 'SCAN_FAILED':
      return 'Not available: it was never scanned clean.';
    default:
      return 'Only a file the malware scan has cleared.';
  }
}

/**
 * The file a download answered with, as bytes - once its SHA-256 is the one
 * the batch was uploaded with (the server re-hashed it too; this checks the
 * journey). Throws, saying so, otherwise.
 *
 * @param {{ contentBase64: string, sha256: string }} answer GET intake/batches/{id}/file's body
 * @param {string} expectedSha256 the batch's sha256, as the list showed it
 * @returns {Promise<Uint8Array>}
 */
export async function downloadedBytes(answer, expectedSha256) {
  const bytes = bytesOfBase64(answer?.contentBase64 ?? '');
  const actual = await sha256Hex(bytes);
  if (actual !== answer?.sha256 || actual !== expectedSha256) throw new Error('the file did not arrive intact. Try again.');
  return bytes;
}

/**
 * The upload's params - exactly what INTAKE_UPLOAD's code is bound to - and
 * the bytes to post on the ticket's form, or a sentence saying why the file
 * cannot be sent. A Core data file (IP-103) carries its loadKind, and its
 * date must be the kind's (uploadKinds.js dateRule: DAILY today, INITIAL
 * before today); a NAV file and a statement carry no kind, as before.
 *
 * @param {{ bytes: ArrayBuffer|Uint8Array, fileName: string, fileClass: string, businessDate: string, accountId?: string|null,
 *           loadKind?: 'INITIAL'|'DAILY'|null, today?: string }} input
 * @returns {Promise<{ params: object, bytes: Uint8Array } | { error: string }>}
 */
export async function prepareUpload({ bytes, fileName, fileClass, businessDate, accountId = null, loadKind = null, today = colomboToday() }) {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (view.length === 0) return { error: fileName + ' is empty.' };
  if (view.length > MAX_UPLOAD_BYTES) return { error: fileName + ' is over the 4 MB limit.' };
  if (!/\.csv$/i.test(fileName)) return { error: fileName + ' is not a CSV file.' };
  if (fileClass === 'BANK_STATEMENT' && !accountId) return { error: 'Choose the bank account the statement is for.' };
  const kinded = takesLoadKind(fileClass);
  if (kinded && loadKind !== 'DAILY' && loadKind !== 'INITIAL') return { error: 'Choose whether this is a daily file or an initial load.' };
  if (kinded) {
    const problem = dateProblem(dateRule(fileClass, loadKind).rule, businessDate, today);
    if (problem) return { error: problem };
  }
  const params = {
    fileClass,
    businessDate,
    fileName,
    sha256: await sha256Hex(view),
    contentLength: view.length,
  };
  if (kinded) params.loadKind = loadKind;
  if (fileClass === 'BANK_STATEMENT') params.accountId = accountId;
  return { params, bytes: view };
}

/** postToStore's code for a POST that got no HTTP answer at all. */
export const STORE_UNREACHABLE = 'STORE_UNREACHABLE';

const XML_ENTITIES = Object.freeze({ '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&apos;': "'" });

/** One element's text from S3's XML error body, or null. */
function xmlText(xml, tag) {
  const match = new RegExp('<' + tag + '>([^<]*)</' + tag + '>').exec(xml ?? '');
  return match ? match[1].replace(/&(amp|lt|gt|quot|apos);/g, (entity) => XML_ENTITIES[entity]) : null;
}

/**
 * Post the file on the ticket's presigned form.
 *
 * The form's fields go first, in the order the server gave them, and the
 * file LAST - S3 ignores every field after the file. The request carries no
 * header of its own: no Authorization and no X-Lfoc-Request, so the Console's
 * access token never leaves for another origin, and a POST of form data with
 * no custom header is a CORS "simple request" that needs no preflight. The
 * form's signature is the only credential S3 sees; credentials: 'omit' keeps
 * the browser's cookies out of it too.
 *
 * A network failure (fetch's TypeError - which is also how a browser reports
 * an answer it may not read across origins) is tried once more while the form
 * is still in date; an HTTP answer never is, because S3 would only refuse the
 * same form again.
 *
 * @param {{ url: string, fields: Record<string, string>, bytes: Uint8Array, fileName: string,
 *           expiresAt?: string|null, fetchImpl?: typeof fetch, now?: () => number }} args
 * @returns {Promise<{ ok: true, status: number, versionId: string|null } | { ok: false, status: number, code: string, message: string }>}
 */
export async function postToStore({ url, fields, bytes, fileName, expiresAt = null, fetchImpl = (...args) => fetch(...args), now = () => Date.now() }) {
  const form = () => {
    const body = new FormData();
    for (const [name, value] of Object.entries(fields)) body.append(name, value);
    body.append('file', new Blob([bytes], { type: 'text/csv' }), fileName);
    return body;
  };
  for (let attempt = 1; ; attempt += 1) {
    let response;
    try {
      response = await fetchImpl(url, { method: 'POST', body: form(), credentials: 'omit', mode: 'cors' });
    } catch (error) {
      const inDate = expiresAt === null || now() < Date.parse(expiresAt);
      if (attempt === 1 && inDate) continue;
      return { ok: false, status: 0, code: STORE_UNREACHABLE, message: error?.message ?? String(error) };
    }
    if (response.ok) return { ok: true, status: response.status, versionId: response.headers?.get?.('x-amz-version-id') ?? null };
    let xml = '';
    try {
      xml = await response.text();
    } catch {
      xml = '';
    }
    return { ok: false, status: response.status, code: xmlText(xml, 'Code') ?? 'HTTP_' + response.status, message: xmlText(xml, 'Message') ?? '' };
  }
}

/** S3's refusals of bytes whose checksum is not the one the form names. */
const CHECKSUM_CODES = new Set(['BadDigest', 'InvalidDigest', 'XAmzContentChecksumMismatch', 'XAmzContentSHA256Mismatch']);

/**
 * Words a person can act on for postToStore's refusal. S3 answers an expired
 * form, an altered one and a wrong key alike - 403 AccessDenied - and only
 * its Message tells an expiry ("Policy expired") from the rest.
 */
export function storeErrorMessage(code, message = '') {
  if (code === 'AccessDenied' && /expired/i.test(message ?? '')) {
    return 'The upload link expired before the file was sent: it lasts 5 minutes from the moment you entered your code.';
  }
  if (code === 'AccessDenied') return 'The upload link expired or was altered before the file was sent.';
  if (CHECKSUM_CODES.has(code)) return 'The file changed after it was fingerprinted, so the store refused it.';
  if (code === 'EntityTooSmall' || code === 'EntityTooLarge') return 'The file\'s size changed after it was chosen, so the store refused it.';
  if (code === STORE_UNREACHABLE) return 'The file store could not be reached, or refused the file without saying why.';
  return 'The file store refused the file (' + code + ').';
}

/** What follows a refused or unsent form: its batch holds the file's fingerprint until it lapses. */
export const NOT_STORED = 'Nothing was stored. The upload shows SCAN_FAILED ' + SCAN_LAPSE_MINUTES
  + ' minutes after it was made, and the same file can be uploaded again then.';

/**
 * The words for a refused upload: the API's own, except a duplicate of a
 * batch still waiting for its scan, which says when it will clear.
 */
export function uploadErrorMessage(error) {
  if (error?.code === 'INTAKE_DUPLICATE_FILE' && error?.details?.priorState === 'SCANNING') {
    return 'An earlier upload of this file is still waiting for its scan. It lapses ' + SCAN_LAPSE_MINUTES
      + ' minutes after it was uploaded, and the file can be uploaded again then.';
  }
  // IP-103: the registry's who-may claims, beyond the upload's own (a daily transactions file is the dealing maker's too).
  if (error?.code === 'LOAD_NOT_PERMITTED') return missingClaimMessage(error, 'upload');
  if (error?.code === 'FILE_CLASS_NOT_AVAILABLE') return 'This kind of file cannot be uploaded yet: the Core does not take it in this release.';
  return error?.message ?? String(error);
}

/** LOAD_NOT_PERMITTED (403) in words: the claim the registry named (details.claim), for the upload or the accept. */
function missingClaimMessage(error, leg) {
  const claim = error?.details?.claim;
  return claim ? 'To ' + leg + ' this file you also need the claim ' + claim + '. Ask an administrator.' : (error?.message ?? String(error));
}

/**
 * How a batch's verdict is announced, once it leaves SCANNING.
 *
 * @returns {{ type: 'success'|'warning'|'error'|'info', text: string }}
 */
export function verdictNotice(batch) {
  const name = batch?.fileName ?? 'The file';
  // A price file is accepted by the system once its scan passes (migration 0016): the poll may see it VALIDATED, or already priced.
  if (acceptedBySystem(batch)) {
    if (batch.state === 'VALIDATED') return { type: 'success', text: name + ' is VALIDATED: its unit prices are being created.' };
    if (batch.state === 'PROCESSED') return { type: 'success', text: name + ' is checked and its unit prices are created: approve them on Fund Register.' };
    if (batch.state === 'REJECTED' && String(batch.rejectedBy ?? '').toLowerCase() === SYSTEM_ACCEPTOR) {
      return { type: 'warning', text: name + ': unit prices were not created. Open it to see why.' };
    }
  }
  switch (batch?.state) {
    case 'VALIDATED':
      return { type: 'success', text: name + ' is VALIDATED: another person can now accept it.' };
    case 'QUARANTINED':
      return { type: 'warning', text: name + ' is QUARANTINED: some of its rows have errors. Open it to see the findings.' };
    case 'REJECTED_MALWARE':
      return { type: 'error', text: name + ' is REJECTED_MALWARE: the malware scan found a threat, and nothing in it is used.' };
    case 'SCAN_FAILED':
      return { type: 'error', text: name + ' is SCAN_FAILED' + (batch.scanStatus ? ' (' + batch.scanStatus + ')' : '')
        + ': it could not be checked. Upload it again.' };
    default:
      return { type: 'info', text: name + ' is now ' + (batch?.state ?? 'unknown') + '.' };
  }
}

/** A refusal of an accept or reject that means the batch changed under the checker: the screen reloads it. */
const STALE_DECISION_CODES = new Set(['ACCEPTANCE_EXPIRED', 'INTAKE_NOT_VALIDATED', 'PAYLOAD_MISMATCH', 'INTAKE_BATCH_NOT_FOUND', 'DAILY_NOT_TODAY']);

/** Whether an accept or reject was refused because the batch is no longer what the checker was shown. */
export function isStaleDecision(error) {
  return STALE_DECISION_CODES.has(error?.code);
}

/**
 * The words for a refused accept or reject (IP-96). step-up/start refuses
 * these before any code is asked for, so the checker reads them instead of
 * the code dialog; anything else is the API's own words (a downstream
 * refusal - BOOK_OPEN, UNITS_PENDING, STATEMENT_CHAIN_BROKEN - already says
 * what to do).
 */
export function decisionErrorMessage(error) {
  switch (error?.code) {
    case 'MAKER_IS_CHECKER':
      return 'You uploaded this file, so another person must accept it.';
    case 'ACCEPTANCE_EXPIRED':
      return 'This file was not accepted within 48 hours of being validated, so it has expired and can no longer be accepted. '
        + 'Upload it again to use it.';
    case 'INTAKE_NOT_VALIDATED':
      return 'This file is no longer waiting for a decision' + (error?.details?.state ? ' (it is now ' + error.details.state + ')' : '')
        + '. It has been refreshed.';
    case 'PAYLOAD_MISMATCH':
      return 'This file changed since you opened it. It has been refreshed: check it again before you decide.';
    case 'INTAKE_BATCH_NOT_FOUND':
      return 'This file no longer exists.';
    // IP-103: a daily file is accepted on its own day in Colombo; after midnight it expires.
    case 'DAILY_NOT_TODAY':
      return 'A daily file is accepted on its own day only, and this one is for ' + (error?.details?.businessDate ?? 'an earlier day')
        + '. Upload today\'s file instead. It has been refreshed.';
    case 'LOAD_NOT_PERMITTED':
      return missingClaimMessage(error, 'accept or reject');
    // The accept ran out of its 20 seconds and changed nothing: the file is still VALIDATED.
    case 'ACCEPT_OUT_OF_TIME':
      return 'The accept took too long and was rolled back: nothing changed, and the file can still be accepted. '
        + 'Try again outside the dealing window.';
    default:
      return error?.message ?? String(error);
  }
}

const SCAN_FAILED_WORDS = Object.freeze({
  NO_VERDICT: 'No malware-scan verdict arrived within ' + SCAN_LAPSE_MINUTES + ' minutes of the upload: the file was never stored '
    + '(the upload was interrupted, or the store refused it), or the scan did not report.',
  FINGERPRINT_MISMATCH: 'The stored file is not the one your code was bound to (its SHA-256 differs), so it was not used.',
  ACCESS_DENIED: 'The malware scan was not allowed to read the file.',
  UNSUPPORTED: 'The malware scan could not scan this kind of file.',
  FAILED: 'The malware scan failed.',
  POST_SCAN_ACTION_FAILED: 'The malware scan finished but could not record its result on the file.',
});

/**
 * What a batch's state means, for the detail drawer: a sentence for every
 * state a person has to act on or wait for, and null for the rest (PROCESSED,
 * ACCEPTED, SUPERSEDED speak for themselves) - but a price file's (migration
 * 0016: the system accepts it, intake/auto-accept.js), whose prices are made,
 * or were not, without anyone accepting it.
 *
 * @param {object} batch mapBatch's shape
 * @param {number} [nowMs]
 * @returns {{ type: 'info'|'success'|'warning'|'error', title: string, text: string } | null}
 */
export function stateNotice(batch, nowMs = Date.now()) {
  if (acceptedBySystem(batch)) {
    const priced = priceFileNotice(batch);
    if (priced !== undefined) return priced;
  }
  switch (batch?.state) {
    case 'SCANNING': {
      const lapsesAt = batch.createdAt ? Date.parse(batch.createdAt) + SCAN_VERDICT_TIMEOUT_MS : null;
      const left = lapsesAt === null ? null : Math.max(0, Math.ceil((lapsesAt - nowMs) / 60000));
      return {
        type: 'info',
        title: 'Being scanned for malware',
        text: 'The file is checked as soon as the scan reports, usually within a minute or two. With no verdict '
          + SCAN_LAPSE_MINUTES + ' minutes after the upload it shows SCAN_FAILED'
          + (left === null ? '' : left === 0 ? ' at the next refresh.' : ' (in about ' + left + ' minute' + (left === 1 ? '' : 's') + ').'),
      };
    }
    case 'VALIDATED': {
      // IP-103: a daily file lapses at midnight in Colombo, the end of its own day.
      if (batch.loadKind === 'DAILY') {
        return {
          type: 'info',
          title: 'Ready for a decision',
          text: 'Scanned and checked. A daily file is for its own day: another person can accept it until midnight (Colombo time) on '
            + batch.businessDate + ', or reject it. Each asks for a code from their authenticator app.',
        };
      }
      const by = batch.validatedAt ? new Date(Date.parse(batch.validatedAt) + ACCEPTANCE_EXPIRY_MS) : null;
      return {
        type: 'info',
        title: 'Ready for a decision',
        text: 'Scanned and checked. Another person can accept it' + (by ? ' until ' + by.toLocaleString('en-GB', {
          timeZone: 'Asia/Colombo', dateStyle: 'medium', timeStyle: 'short',
        }) : ' for 48 hours after it was validated') + ', or reject it. Each asks for a code from their authenticator app.',
      };
    }
    case 'QUARANTINED':
      return {
        type: 'warning',
        title: 'Quarantined',
        text: (batch.errorCount ?? 0) + ' row' + (batch.errorCount === 1 ? ' has an error' : 's have errors')
          + ', so this file cannot be accepted. Correct the file and upload it again.',
      };
    case 'REJECTED_MALWARE':
      return {
        type: 'error',
        title: 'Malware found',
        text: 'The malware scan found a threat in this file. Nothing in it is used, and an administrator has been alerted.',
      };
    case 'SCAN_FAILED':
      return {
        type: 'error',
        title: 'Not checked' + (batch.scanStatus ? ' (' + batch.scanStatus + ')' : ''),
        text: (SCAN_FAILED_WORDS[batch.scanStatus] ?? 'The file could not be checked.') + ' Nothing in it is used. Upload it again'
          + (batch.scanStatus === 'NO_VERDICT' || batch.scanStatus === 'FINGERPRINT_MISMATCH' ? '.' : ', and tell an administrator if it happens twice.'),
      };
    case 'ACCEPTANCE_EXPIRED':
      return batch.loadKind === 'DAILY'
        ? { type: 'warning', title: 'Expired', text: 'Nobody accepted this daily file on its own day (' + batch.businessDate + '). Upload today\'s file to use one.' }
        : { type: 'warning', title: 'Expired', text: 'Nobody accepted it within 48 hours of its validation. Upload it again to use it.' };
    case 'REJECTED':
      return { type: 'info', title: 'Rejected', text: batch.rejectReason ? 'Reason: ' + batch.rejectReason : 'Rejected by a checker.' };
    default:
      return null;
  }
}

/** A price file's VALIDATED, PROCESSED and system-REJECTED states, in words; undefined for the states every file shares. */
function priceFileNotice(batch) {
  switch (batch.state) {
    case 'VALIDATED':
      return {
        type: 'info',
        title: 'Creating unit prices',
        text: 'Scanned and checked. A NAV run is being made for each fund in the file; refresh in a moment, then approve them on Fund Register.',
      };
    case 'PROCESSED':
      return {
        type: 'success',
        title: 'Unit prices created',
        text: 'A NAV run was made for each fund in the file. Another person approves each one on Fund Register, which publishes its price.',
      };
    case 'REJECTED':
      if (String(batch.rejectedBy ?? '').toLowerCase() !== SYSTEM_ACCEPTOR) return undefined;
      return {
        type: 'warning',
        title: 'Unit prices not created',
        text: (batch.rejectReason ?? 'The file could not be priced.') + ' Upload the file again when it can be priced.',
      };
    default:
      return undefined;
  }
}

/** The SCANNING poll: 3 s, then 1.5 times longer each tick up to 15 s, for at most 3 minutes per batch. */
export const SCAN_POLL = Object.freeze({ firstMs: 3000, maxMs: 15000, factor: 1.5, timeoutMs: 180000 });

const sleepFor = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * One poll for a screen, however many of its uploads are scanning. Each tick
 * is ONE call - the batches still SCANNING - and a watched batch missing from
 * the answer has its verdict (onSettled). The tick backs off, and a batch is
 * watched for at most timeoutMs (onGaveUp): the poll must not keep an idle
 * session alive, nor spend the per-session request allowance on one file.
 * A failed poll is retried at the next tick; stop() ends it for good.
 *
 * @param {{
 *   listScanning: () => Promise<string[]>,      the batch ids now SCANNING
 *   onSettled: (batchId: string) => void,
 *   onGaveUp: (batchId: string) => void,
 *   sleep?: (ms: number) => Promise<void>,
 *   now?: () => number,
 *   firstMs?: number, maxMs?: number, factor?: number, timeoutMs?: number,
 * }} options
 */
export function createScanWatch({ listScanning, onSettled, onGaveUp, sleep = sleepFor, now = () => Date.now(), ...timing }) {
  const { firstMs, maxMs, factor, timeoutMs } = { ...SCAN_POLL, ...timing };
  const deadlines = new Map();
  let delay = firstMs;
  let stopped = false;
  let running = null;

  async function loop() {
    try {
      while (!stopped && deadlines.size) {
        await sleep(delay);
        if (stopped) break;
        let scanning = null;
        try {
          scanning = new Set(await listScanning());
        } catch {
          scanning = null;
        }
        const at = now();
        for (const [batchId, deadline] of [...deadlines]) {
          if (stopped) break;
          if (scanning && !scanning.has(batchId)) {
            deadlines.delete(batchId);
            onSettled(batchId);
          } else if (at >= deadline) {
            deadlines.delete(batchId);
            onGaveUp(batchId);
          }
        }
        delay = Math.min(Math.round(delay * factor), maxMs);
      }
    } finally {
      running = null;
    }
  }

  return {
    /** Watch one more batch; a new upload starts the backoff again. */
    watch(batchId) {
      if (stopped) return;
      deadlines.set(batchId, now() + timeoutMs);
      delay = firstMs;
      running ??= loop();
    },
    stop() {
      stopped = true;
      deadlines.clear();
    },
    watching: () => [...deadlines.keys()],
    /** Resolves when the loop has nothing left to watch (tests). */
    idle: () => running ?? Promise.resolve(),
  };
}
